/**
 * Página de listagem de classificações
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Layout, Loading, ErrorMessage, SearchBar } from '@components';
import { ListaClassificacao } from '@components/classificacao/ListaClassificacao';
import { useFetch, useFilter, useDebounce } from '@hooks';
import { classificacaoController } from '@controllers';
import { useSessionContext, useAuthContext } from '@contexts';
import { Classificacao, ROUTES } from '@types';
import { SessionUtil } from '@utils';
import theme from '@theme';

type OrderBy = 'nome-asc' | 'nome-desc' | 'total-asc' | 'total-desc';

export const ListaClassificacaoPage: React.FC = () => {
  const navigate = useNavigate();
  const { setLastClassificacao } = useSessionContext();
  const { operationType } = useAuthContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState<OrderBy>('nome-asc');

  const debouncedSearch = useDebounce(searchTerm, 300);

  // Redireciona se houver previsão ou pedido ativo
  useEffect(() => {
    console.log('[ListaClassificacao] operationType recebido:', operationType);

    if (operationType === 'previsao') {
      console.log('[ListaClassificacao] Previsão ativa, redirecionando para CLASSIFICACAO_PREVISAO');
      navigate(ROUTES.CLASSIFICACAO_PREVISAO);
    } else if (operationType === 'pedido') {
      console.log('[ListaClassificacao] Pedido ativo, redirecionando para CLASSIFICACAO_PEDIDO');
      navigate(ROUTES.CLASSIFICACAO_PEDIDO);
    }
  }, [operationType, navigate]);

  // Carrega classificações
  const { data: classificacoes, isLoading, error, refetch } = useFetch(
    () => classificacaoController.obterClassificacoes()
  );

  // Filtra por busca
  const { filteredData } = useFilter(classificacoes || [], ['nome']);

  // Ordena
  const sortedData = React.useMemo(() => {
    if (!filteredData) return [];

    const [field, direction] = orderBy.split('-') as [
      'nome' | 'total',
      'asc' | 'desc'
    ];

    if (field === 'nome') {
      return classificacaoController.ordenarPorNome(filteredData, direction);
    } else {
      return classificacaoController.ordenarPorTotal(filteredData, direction);
    }
  }, [filteredData, orderBy]);

  const handleSelectClassificacao = (classificacao: Classificacao) => {
    // Salva última classificação acessada
    setLastClassificacao(classificacao.codigo);

    // Log para debug
    console.log('ListaClassificacao - operationType:', operationType);
    console.log('ListaClassificacao - vendedor do localStorage:', SessionUtil.getVendedor());

    // Navega para produtos dessa classificação baseado no tipo de operação
    if (operationType === 'previsao') {
      console.log('Navegando para previsão:', `/produto-previsao/${classificacao.codigo}`);
      navigate(`/produto-previsao/${classificacao.codigo}`);
    } else if (operationType === 'pedido') {
      console.log('Navegando para pedido:', `/produto-pedido/${classificacao.codigo}`);
      navigate(`/produto-pedido/${classificacao.codigo}`);
    } else {
      console.log('Navegando para produtos:', `/produtos/${classificacao.codigo}`);
      navigate(`/produtos/${classificacao.codigo}`);
    }
  };

  if (isLoading) {
    return (
      <Layout title="Categorias">
        <Loading message="Carregando categorias..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Categorias">
        <ErrorMessage
          title="Erro ao carregar categorias"
          message={error.message}
          onRetry={refetch}
        />
      </Layout>
    );
  }

  return (
    <Layout title="Categorias" showHeader showFooter footerType="default">
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar categoria..."
          />
        </Box>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={orderBy}
            label="Ordenar por"
            onChange={(e) => setOrderBy(e.target.value as OrderBy)}
            sx={{ bgcolor: theme.palette.elite.contrastText }}
          >
            <MenuItem value="nome-asc">Nome (A-Z)</MenuItem>
            <MenuItem value="nome-desc">Nome (Z-A)</MenuItem>
            <MenuItem value="total-desc">Mais produtos</MenuItem>
            <MenuItem value="total-asc">Menos produtos</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <ListaClassificacao
        classificacoes={sortedData}
        onSelectClassificacao={handleSelectClassificacao}
        isSearching={!!debouncedSearch}
      />
    </Layout>
  );
};

export default ListaClassificacaoPage;